import loadable from '@loadable/component';
import { toTruncated } from '@simplywallst/formatting';
import ChartWrapper from '@/pages/CompanyReport/components/Overview/components/ChartWrapper';
import ReportSection from '@components/ReportSection';
import ReportSubSection from '@components/ReportSubSection';
import ReportSectionHeader from '@components/ReportSectionHeader';
import { Leader } from '@/pages/CompanyReport/components/Management';
import ListTruncator from '@components/ListTruncator';
import Statement from '@/components/Statement';
import Description from '@/components/Statement/components/Description';
import { LegacyAnchor } from '@/constants/anchors';
import PageAnchor from '@components/PageAnchor';
import type { ManagementStatements } from '@/pages/CompanyReport/components/Management/redux/selectors';
import type { ViewMode } from '@/pages/CompanyReport/enum';
import type { People } from '@/pages/CompanyReport/redux/interface';
import { BrazeContentCardWrapper } from '@/pages/CompanyReport/components/BrazeContentCard';
import CEOCompensationTable from './components/CEOCompensationTable';
import { COMPANY_CEO_COMPENSATION } from '@/constants/feedbackSections';
import { getCompanyInfographicDownloadUrl } from '@/utilities/urlGenerator';
import { ChartType } from '@/constants/downloadSections';
import CompanySectionTitle from '@/pages/CompanyReport/components/CompanySectionTitle';
import { useVisibleSectionsContext } from '@/pages/CompanyReport/components/VisibleSectionsContextProvider';

const BrazeContentCard = loadable(() => import('@components/BrazeContentCard'));

const TeamTenure = loadable(
  () => import('@/pages/CompanyReport/components/Management'),
  {
    resolveComponent: (component) => component.TeamTenure,
  }
);
const TeamList = loadable(
  () => import('@/pages/CompanyReport/components/Management'),
  {
    resolveComponent: (component) => component.TeamList,
  }
);
const CEOCompensationChart = loadable(
  () => import('./components/CEOCompensationChart'),
  {
    ssr: false,
  }
);

export type TeamType = 'management' | 'board';

export interface Member extends Omit<People, 'compensation' | 'tenure'> {
  compensation: string;
  formattedLeaderTenure: string;
  formattedBoardTenure: string;
  formattedManagementTenure: string;
}

export interface ManagementProps {
  board: Member[];
  boardAge: number;
  hasCEO: boolean;
  leaderAge: number;
  companyId: string;
  uniqueSymbol: string;
  companyName: string;
  boardTenure: number | null;
  currencyISO: string;
  reportingUnit: number;
  leaderTenure: string;
  leaderTenureFormatted: string;
  sectionNumber: number;
  managementAge: number;
  currencySymbol: string;
  leaderYearBorn: number;
  managementTeam: Member[];
  leaderLastName: string;
  leaderBiography: string;
  managementTenure: number | null;
  leaderSalutation: string;
  leaderCompensation: string;
  statements: ManagementStatements;
  keyMetric: string;
  keyMetricTitle: string;
  viewMode: ViewMode;
  marketCapListing: number;
  /**
   * CEO Compensation
   */
  earnings: number[][];
  earningsMax: number;
  earningsMin: number;
  salary: number[][];
  totalCompensation: number[][];
  salaryMax: number;
  salaryMin: number;
  totalCompensationMax: number;
  totalCompensationMin: number;
  CEOCompensationDayZero?: number;
}

const Management = ({
  board,
  boardAge,
  leaderAge,
  hasCEO,
  companyId,
  companyName,
  boardTenure,
  currencyISO,
  reportingUnit,
  leaderTenureFormatted,
  sectionNumber,
  managementAge,
  currencySymbol,
  leaderYearBorn,
  managementTeam,
  leaderLastName,
  leaderBiography,
  managementTenure,
  leaderSalutation,
  leaderCompensation,
  statements: {
    isCEOCompensationAppropriate,
    isCEOCompensationChangeJustified,
    isManagementTeamSeasoned,
    isBoardSeasoned,
  },
  keyMetric,
  keyMetricTitle,
  viewMode,
  marketCapListing,
  /**
   * CEO Compensation
   */
  earnings,
  earningsMax,
  earningsMin,
  salary,
  totalCompensation,
  salaryMax,
  salaryMin,
  totalCompensationMax,
  totalCompensationMin,
  CEOCompensationDayZero,
}: ManagementProps) => {
  const { isPristine } = useVisibleSectionsContext();
  const subSectionTitleElement = isPristine ? 'h2' : 'h3';
  const ceoDetailsDownloadUrl = getCompanyInfographicDownloadUrl(
    companyId,
    ChartType.CEOCompensation
  );
  return (
    <ReportSection
      sectionNumber={sectionNumber}
      data-cy-id="management-section"
    >
      <ReportSectionHeader
        title={<CompanySectionTitle title={'Management'} />}
        sectionDescription={`How experienced are the management team and are they aligned to shareholders interests?`}
        keyMetric={keyMetric}
        keyMetricTitle={keyMetricTitle}
      />
      <BrazeContentCardWrapper>
        <BrazeContentCard
          feedType="company-overview.management"
          anchorTarget="_blank"
        />
      </BrazeContentCardWrapper>
      <PageAnchor type="sub-section-title" id={LegacyAnchor.MANAGEMENT_CEO} />
      <ReportSubSection
        titleElement={subSectionTitleElement}
        title="CEO"
        body={
          <Leader
            hasCEO={hasCEO}
            leaderAge={leaderAge}
            companyName={companyName}
            leaderTenure={leaderTenureFormatted}
            leaderYearBorn={leaderYearBorn}
            leaderLastName={leaderLastName}
            leaderBiography={leaderBiography}
            leaderSalutation={leaderSalutation}
            leaderCompensation={leaderCompensation}
            viewMode={viewMode}
          />
        }
      />
      <PageAnchor type="sub-section-title" id={LegacyAnchor.CEO_SALARY} />
      <hr />
      {salary !== null && salary.length > 0 && earnings.length > 0 && (
        <>
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title="CEO Compensation Analysis"
            body={
              <ChartWrapper
                id="management-ceo-compensation-chart"
                chart={
                  <CEOCompensationChart
                    currencyISO={currencyISO}
                    reportingUnit={reportingUnit}
                    earnings={earnings}
                    earningsMax={earningsMax}
                    earningsMin={earningsMin}
                    salary={salary}
                    salaryMax={salaryMax}
                    salaryMin={salaryMin}
                    totalCompensation={totalCompensation}
                    totalCompensationMax={totalCompensationMax}
                    totalCompensationMin={totalCompensationMin}
                    dayZero={CEOCompensationDayZero}
                  />
                }
                table={<CEOCompensationTable />}
                feedbackLabel={COMPANY_CEO_COMPENSATION}
                exportDarkUrl={ceoDetailsDownloadUrl}
                preRenderHeight={490}
                preRenderHeightMdDown={470}
                preRenderHeightSmDown={460}
              />
            }
            statements={
              <>
                <Statement
                  title={isCEOCompensationAppropriate.title}
                  description={
                    <Description statement={isCEOCompensationAppropriate} />
                  }
                  value={isCEOCompensationAppropriate.value}
                />
                <Statement
                  title={isCEOCompensationChangeJustified.title}
                  description={
                    <Description statement={isCEOCompensationChangeJustified} />
                  }
                  value={isCEOCompensationChangeJustified.value}
                />
              </>
            }
          />
          <hr />
        </>
      )}
      {managementTenure !== null && managementTenure > 0 && (
        <>
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title="Leadership Team"
            trackingProps={{ subject: 'Management Team' }}
            body={
              <>
                <ListTruncator viewMode={viewMode} minHeight={200}>
                  <TeamList
                    dataCyId={'leadership-team'}
                    team={managementTeam}
                    teamType="management"
                    marketCapListing={marketCapListing}
                    currencySymbol={currencySymbol}
                    viewMode={viewMode}
                  />
                </ListTruncator>
                <TeamTenure
                  dataCyId={'leadership-team'}
                  tenure={toTruncated(managementTenure, 1)}
                  averageAge={managementAge}
                />
              </>
            }
            statements={
              <Statement
                title={isManagementTeamSeasoned.title}
                description={
                  <Description statement={isManagementTeamSeasoned} />
                }
                value={isManagementTeamSeasoned.value}
                statementId="isManagementTeamSeasoned"
              />
            }
          />
          <hr />
        </>
      )}
      {boardTenure !== null && boardTenure > 0 ? (
        <>
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title="Board Members"
            trackingProps={{ subject: 'Board Members' }}
            body={
              <>
                <ListTruncator viewMode={viewMode} minHeight={200}>
                  <TeamList
                    dataCyId={'board-members'}
                    team={board}
                    teamType="board"
                    marketCapListing={marketCapListing}
                    currencySymbol={currencySymbol}
                    viewMode={viewMode}
                  />
                </ListTruncator>
                <TeamTenure
                  dataCyId={'board-members'}
                  tenure={toTruncated(boardTenure, 1)}
                  averageAge={boardAge}
                />
              </>
            }
            statements={
              <Statement
                title={isBoardSeasoned.title}
                description={<Description statement={isBoardSeasoned} />}
                value={isBoardSeasoned.value}
                statementId="isBoardSeasoned"
              />
            }
          />
          <hr />
        </>
      ) : null}
    </ReportSection>
  );
};

export default Management;
